import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Brain, CloudSun, CheckCircle2, AlertTriangle, MapPin, Loader2, Wallet } from "lucide-react";
import { Input } from "@/components/ui/input";

const StatusPill = ({ tone, children, ...rest }) => (
  <span {...rest} className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${tone}`}>{children}</span>
);

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function SeasonalityConfig() {
  const [cfg, setCfg] = useState(null);
  const [meta, setMeta] = useState({ phases: [], phase_labels: {}, all_categories: [] });
  const [overrides, setOverrides] = useState({});
  const [category, setCategory] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/intelligence/seasonality").then(({ data }) => {
    setCfg(data.categories);
    setMeta({ phases: data.phases, phase_labels: data.phase_labels, all_categories: data.all_categories });
    setOverrides(data.overrides || {});
    setCategory((c) => c || data.all_categories[0]);
  }).catch(() => {});

  useEffect(() => { load(); }, []);

  const applyOverride = async (direction) => {
    try {
      await api.post("/intelligence/seasonality/override", { category, direction, days: 7 });
      toast.success(`Override set for ${category} (7 days)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not set override"); }
  };
  const clearOverride = async () => {
    try {
      await api.delete(`/intelligence/seasonality/override/${encodeURIComponent(category)}`);
      toast.success(`Override cleared for ${category}`);
      load();
    } catch { toast.error("Could not clear override"); }
  };

  if (!cfg) return <div className="bg-white rounded-2xl border border-border p-5 text-sm text-muted-foreground">Loading seasonality...</div>;

  const phases = cfg[category] || [];
  const setMonth = (phaseKey, field, val) => {
    setCfg((prev) => ({
      ...prev,
      [category]: (prev[category] || []).map((p) => p.phase === phaseKey ? { ...p, [field]: Number(val) } : p),
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/intelligence/seasonality", { categories: { [category]: cfg[category] } });
      toast.success(`${category} seasonality saved`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-border p-5" data-testid="seasonality-config-card">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-xl bg-navy/5 flex items-center justify-center"><Brain className="w-5 h-5 text-navy" /></div>
        <div>
          <div className="font-bold text-navy">Dealer Seasonality</div>
          <div className="text-xs text-muted-foreground">Tell RecJet Mind your local selling seasons per category. Treated as an intelligence input, refine anytime.</div>
        </div>
      </div>

      <div className="mt-4 mb-3 max-w-xs">
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="seasonality-category-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white max-h-72">
            {meta.all_categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mb-4 rounded-xl border border-dashed border-border bg-secondary/40 p-3" data-testid="seasonality-override">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-xs text-muted-foreground max-w-md">
            Temporary override: nudge this category one phase for 7 days when local conditions differ from the calendar. It returns to normal automatically when it expires.
          </div>
          {overrides[category]?.expires_at && (
            <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-gold/15 text-gold border border-gold/40" data-testid="seasonality-override-active">
              Active until {new Date(overrides[category].expires_at).toLocaleDateString()}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-2">
          <Button size="sm" variant="outline" onClick={() => applyOverride(1)} data-testid="override-advance">Advance one phase</Button>
          <Button size="sm" variant="outline" onClick={() => applyOverride(-1)} data-testid="override-holdback">Hold back one phase</Button>
          {overrides[category] && <Button size="sm" variant="ghost" onClick={clearOverride} data-testid="override-clear">Clear</Button>}
        </div>
      </div>

      <div className="space-y-2">
        {phases.map((p) => (
          <div key={p.phase} className="flex items-center gap-3 flex-wrap" data-testid={`seasonality-phase-${p.phase}`}>
            <div className="w-28 text-sm font-semibold text-navy">{meta.phase_labels[p.phase] || p.phase}</div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>From</span>
              <Select value={String(p.start_month)} onValueChange={(v) => setMonth(p.phase, "start_month", v)}>
                <SelectTrigger className="w-24 h-9" data-testid={`seasonality-${p.phase}-start`}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
              </Select>
              <span>to</span>
              <Select value={String(p.end_month)} onValueChange={(v) => setMonth(p.phase, "end_month", v)}>
                <SelectTrigger className="w-24 h-9" data-testid={`seasonality-${p.phase}-end`}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        ))}
      </div>

      <Button onClick={save} disabled={saving} className="mt-4 bg-navy hover:bg-navy-light text-white" data-testid="seasonality-save-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Save {category} seasons
      </Button>
    </div>
  );
}

export function MediaPlanEditor() {
  const [plan, setPlan] = useState(null);
  const [max, setMax] = useState(200);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/intelligence/media-plan").then(({ data }) => {
    setPlan(data); setMax(data.normal_campaign_max);
  }).catch(() => setPlan(null));
  useEffect(() => { load(); }, []);

  const setField = (m, field, value) => {
    setPlan((p) => ({ ...p, months: { ...p.months,
      [m]: { ...p.months[m], [field]: field === "operating_mode" ? value : (value === "" ? 0 : Number(value)) } } }));
  };

  const total = (mp) => (Number(mp.meta_budget) || 0) + (Number(mp.google_budget) || 0) + (Number(mp.reserve) || 0);

  const save = async () => {
    setSaving(true);
    try {
      const months = {};
      Object.entries(plan.months).forEach(([k, v]) => {
        months[k] = { operating_mode: v.operating_mode, meta_budget: Number(v.meta_budget) || 0,
          google_budget: Number(v.google_budget) || 0, reserve: Number(v.reserve) || 0 };
      });
      const { data } = await api.put("/intelligence/media-plan", { normal_campaign_max: Number(max) || 0, months });
      setPlan(data); setMax(data.normal_campaign_max);
      toast.success("Monthly Media Plan saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save media plan"); }
    finally { setSaving(false); }
  };

  if (!plan) return <div className="bg-white rounded-2xl border border-border p-5 text-sm text-muted-foreground">Loading media plan...</div>;

  const modes = plan.operating_modes || [];
  return (
    <div className="bg-white rounded-2xl border border-border p-5" data-testid="media-plan-card">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-xl bg-gold/15 flex items-center justify-center"><Wallet className="w-5 h-5 text-gold" /></div>
        <div>
          <div className="font-bold text-navy">Monthly Media Plan</div>
          <div className="text-xs text-muted-foreground">Set your paid budget by month and channel. RecJet Mind paces spend inside these amounts and never spends your whole month on one campaign.</div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-dashed border-border bg-secondary/40 p-3">
        <label className="text-sm font-semibold text-navy" htmlFor="normal-campaign-max">Normal Paid Campaign Maximum</label>
        <div className="flex items-center gap-1">
          <span className="text-muted-foreground">$</span>
          <Input id="normal-campaign-max" type="number" min="0" value={max} onChange={(e) => setMax(e.target.value)} className="w-28 h-9" data-testid="normal-campaign-max-input" />
        </div>
        <span className="text-xs text-muted-foreground">Ceiling for a routine single-boat paid campaign. RecJet often recommends less.</span>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-navy">
            <tr className="text-left border-b border-border">
              {["Month", "Operating Mode", "Meta", "Google", "Reserve", "Total"].map((h) => (
                <th key={h} className="px-2 py-2 font-semibold whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border" data-testid="media-plan-body">
            {MONTHS.map((label, i) => {
              const key = String(i + 1);
              const mp = plan.months[key] || {};
              const isCurrent = plan.current_month === (i + 1);
              return (
                <tr key={key} className={isCurrent ? "bg-gold/5" : ""} data-testid={`media-plan-row-${key}`}>
                  <td className="px-2 py-2 font-medium text-navy whitespace-nowrap">{label}{isCurrent && <span className="ml-1 text-[10px] uppercase tracking-wider text-gold font-bold">now</span>}</td>
                  <td className="px-2 py-2">
                    <Select value={mp.operating_mode || plan.suggested_modes?.[key] || ""} onValueChange={(v) => setField(key, "operating_mode", v)}>
                      <SelectTrigger className="w-40 h-9" data-testid={`media-plan-mode-${key}`}><SelectValue placeholder="Mode" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {modes.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-2 py-2"><Input type="number" min="0" value={mp.meta_budget ?? 0} onChange={(e) => setField(key, "meta_budget", e.target.value)} className="w-24 h-9" data-testid={`media-plan-meta-${key}`} /></td>
                  <td className="px-2 py-2"><Input type="number" min="0" value={mp.google_budget ?? 0} onChange={(e) => setField(key, "google_budget", e.target.value)} className="w-24 h-9" data-testid={`media-plan-google-${key}`} /></td>
                  <td className="px-2 py-2"><Input type="number" min="0" value={mp.reserve ?? 0} onChange={(e) => setField(key, "reserve", e.target.value)} className="w-24 h-9" data-testid={`media-plan-reserve-${key}`} /></td>
                  <td className="px-2 py-2 font-semibold text-navy whitespace-nowrap" data-testid={`media-plan-total-${key}`}>${total(mp).toLocaleString()}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Button onClick={save} disabled={saving} className="mt-4 bg-navy hover:bg-navy-light text-white" data-testid="media-plan-save-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Save Monthly Media Plan
      </Button>
    </div>
  );
}

export function WeatherIntelligenceConfig() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/intelligence/weather/status").then(({ data }) => setStatus(data)).catch(() => setStatus(null));
  useEffect(() => { load(); }, []);

  const toggle = async (enabled) => {
    setBusy(true);
    try {
      await api.post("/intelligence/weather/toggle", { enabled });
      toast.success(enabled ? "Weather intelligence enabled" : "Weather intelligence disabled");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update weather setting"); }
    finally { setBusy(false); }
  };

  if (!status) return <div className="bg-white rounded-2xl border border-border p-5 text-sm text-muted-foreground">Loading weather...</div>;

  return (
    <div className="bg-white rounded-2xl border border-border p-5" data-testid="weather-config-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-sky-500/10 flex items-center justify-center"><CloudSun className="w-5 h-5 text-sky-600" /></div>
          <div>
            <div className="font-bold text-navy">Weather Intelligence</div>
            <div className="text-xs text-muted-foreground">Local forecast signals that help time your campaigns. Provider: Open-Meteo.</div>
          </div>
        </div>
        {status.enabled
          ? <StatusPill tone="bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="weather-status-on"><CheckCircle2 className="w-3.5 h-3.5" /> Enabled</StatusPill>
          : <StatusPill tone="bg-slate-100 text-slate-600 border-slate-200" data-testid="weather-status-off">Disabled</StatusPill>}
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-muted-foreground" />
          <span className="text-muted-foreground">Location:</span>
          <span className="font-medium text-navy" data-testid="weather-location">{status.location_label || status.dealership_city_state || "Not set"}</span>
        </div>
        <div><span className="text-muted-foreground">Provider:</span> <span className="font-medium text-navy capitalize">{status.provider}</span></div>
        <div><span className="text-muted-foreground">Last update:</span> <span className="font-medium text-navy">{status.last_updated ? new Date(status.last_updated).toLocaleString() : "Never"}</span></div>
        {status.last_error && (
          <div className="flex items-center gap-1.5 text-amber-700" data-testid="weather-error"><AlertTriangle className="w-4 h-4" /> {status.last_error}</div>
        )}
      </div>

      {!status.dealership_city_state && !status.has_coordinates && (
        <p className="text-xs text-amber-700 mt-3">Add your dealership city and state in Dealership Profile so RecJet Mind can resolve the weather location automatically.</p>
      )}

      <div className="mt-4">
        {status.enabled
          ? <Button variant="outline" onClick={() => toggle(false)} disabled={busy} data-testid="weather-disable-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Disable</Button>
          : <Button onClick={() => toggle(true)} disabled={busy} className="bg-navy hover:bg-navy-light text-white" data-testid="weather-enable-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Enable weather intelligence</Button>}
      </div>
    </div>
  );
}
